import { vuAudio } from "../vuAudio.svelte";

/**
 * The friend-bond ceremony sound (~1.6 s, 5 layers, fully synthesized).
 *
 *  Layer 1 — Approach    two glissandos (220→523 Hz, 880→523 Hz) meeting at C5
 *  Layer 2 — Connection  perfect-fifth chime (C5 + G5, triangle "click")
 *  Layer 3 — Bloom       major-chord arpeggio C5→E5→G5→C6 (sine + 3rd harmonic bell)
 *  Layer 4 — Warmth      detuned saw pad on C3 (chorused, low-passed)
 *  Layer 5 — Sparkle     6 random pentatonic tinkles (C major, panned, very quiet)
 *
 * No external assets — every sample is generated by the OscillatorNode
 * and noise routines below. Calling this is safe even if the engine
 * isn't ready: it's a no-op in that case.
 */
export function playBondSound(): void {
  const bus = vuAudio.getBus();
  if (!bus) return;

  const { ctx, dry, wet } = bus;
  const now = ctx.currentTime;

  // 1 — Approach
  approach(ctx, dry, wet, now);

  // 2 — Connection: perfect fifth at +0.35 s
  chime(ctx, dry, wet, now + 0.35, 523.25);
  chime(ctx, dry, wet, now + 0.35, 783.99);

  // 3 — Bloom: ascending major-chord arpeggio
  const bloomNotes = [523.25, 659.25, 783.99, 1046.5];
  bloomNotes.forEach((freq, i) => {
    bell(ctx, dry, wet, now + 0.45 + i * 0.09, freq, 0.7 - i * 0.08);
  });

  // 4 — Warmth: low pad
  pad(ctx, dry, wet, now + 0.2, 130.81);

  // 5 — Sparkle: 6 randomly placed pentatonic tinkles
  const pentatonic = [1046.5, 1174.66, 1318.51, 1567.98, 1760, 2093];
  for (let i = 0; i < 6; i++) {
    const freq = pentatonic[Math.floor(Math.random() * pentatonic.length)];
    sparkle(ctx, dry, wet, now + 0.5 + Math.random() * 0.9, freq);
  }
}

function approach(
  ctx: AudioContext,
  dry: GainNode,
  wet: GainNode,
  t: number,
): void {
  // Rising voice: 220 Hz → C5
  const up = ctx.createOscillator();
  const upGain = ctx.createGain();
  up.type = "sine";
  up.frequency.setValueAtTime(220, t);
  up.frequency.exponentialRampToValueAtTime(523.25, t + 0.4);
  upGain.gain.setValueAtTime(0, t);
  upGain.gain.linearRampToValueAtTime(0.18, t + 0.1);
  upGain.gain.linearRampToValueAtTime(0.0001, t + 0.4);
  up.connect(upGain);
  upGain.connect(dry);
  upGain.connect(wet);
  up.start(t);
  up.stop(t + 0.45);

  // Falling voice: 880 Hz → C5
  const down = ctx.createOscillator();
  const downGain = ctx.createGain();
  down.type = "sine";
  down.frequency.setValueAtTime(880, t);
  down.frequency.exponentialRampToValueAtTime(523.25, t + 0.4);
  downGain.gain.setValueAtTime(0, t);
  downGain.gain.linearRampToValueAtTime(0.14, t + 0.1);
  downGain.gain.linearRampToValueAtTime(0.0001, t + 0.4);
  down.connect(downGain);
  downGain.connect(dry);
  downGain.connect(wet);
  down.start(t);
  down.stop(t + 0.45);
}

function chime(
  ctx: AudioContext,
  dry: GainNode,
  wet: GainNode,
  t: number,
  freq: number,
): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "triangle";
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0, t);
  gain.gain.linearRampToValueAtTime(0.22, t + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.6);
  osc.connect(gain);
  gain.connect(dry);
  gain.connect(wet);
  osc.start(t);
  osc.stop(t + 0.65);
}

function bell(
  ctx: AudioContext,
  dry: GainNode,
  wet: GainNode,
  t: number,
  freq: number,
  amp: number,
): void {
  // Fundamental
  const f = ctx.createOscillator();
  const fGain = ctx.createGain();
  f.type = "sine";
  f.frequency.value = freq;
  fGain.gain.setValueAtTime(0, t);
  fGain.gain.linearRampToValueAtTime(amp * 0.3, t + 0.005);
  fGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.8);
  f.connect(fGain);
  fGain.connect(dry);
  fGain.connect(wet);
  f.start(t);
  f.stop(t + 0.85);

  // 3rd harmonic gives the "bell" timbre — short, much quieter than the
  // fundamental so it adds shimmer without dominating.
  const h = ctx.createOscillator();
  const hGain = ctx.createGain();
  h.type = "sine";
  h.frequency.value = freq * 3;
  hGain.gain.setValueAtTime(0, t);
  hGain.gain.linearRampToValueAtTime(amp * 0.06, t + 0.005);
  hGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
  h.connect(hGain);
  hGain.connect(dry);
  hGain.connect(wet);
  h.start(t);
  h.stop(t + 0.45);
}

function pad(
  ctx: AudioContext,
  dry: GainNode,
  wet: GainNode,
  t: number,
  freq: number,
): void {
  // Two slightly detuned saw oscillators run through a shared low-pass.
  // The ±7 cent split creates a chorus-like beating that reads as "warmth".
  [-7, 7].forEach((cents) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sawtooth";
    osc.frequency.value = freq * Math.pow(2, cents / 1200);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.05, t + 0.4);
    gain.gain.linearRampToValueAtTime(0.04, t + 1.0);
    gain.gain.linearRampToValueAtTime(0.0001, t + 1.4);

    const lpf = ctx.createBiquadFilter();
    lpf.type = "lowpass";
    lpf.frequency.value = 600;
    lpf.Q.value = 0.7;

    osc.connect(lpf).connect(gain);
    gain.connect(dry);
    gain.connect(wet);
    osc.start(t);
    osc.stop(t + 1.45);
  });
}

function sparkle(
  ctx: AudioContext,
  dry: GainNode,
  wet: GainNode,
  t: number,
  freq: number,
): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  const panner = ctx.createStereoPanner();
  panner.pan.value = (Math.random() * 2 - 1) * 0.7;
  osc.type = "sine";
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0, t);
  gain.gain.linearRampToValueAtTime(0.04, t + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.25);
  osc.connect(gain).connect(panner);
  panner.connect(dry);
  panner.connect(wet);
  osc.start(t);
  osc.stop(t + 0.3);
}
